/**
 * Před/Potom comparison slider.
 *
 * etrimon-realestate renders the pair as two stacked, captioned <figure>s, so
 * the block already says "this is before, this is after" with no JS. This
 * script upgrades that to an overlay with a drag handle: the after pane is
 * absolutely positioned over the before pane and clipped to a percentage.
 *
 * The overlay is applied by adding .listing-beforeafter--live, never by the
 * server, for the reason documented in ListingDetail::render_before_after_block()
 * — a server-rendered overlay with no script would bury the before image
 * permanently.
 *
 * ES5 + IIFE + var + Yoda conditions, matching main.js. No jQuery: the
 * front-end bundle declares no dependencies.
 */
( function () {
	'use strict';

	var MIN = 0;
	var MAX = 100;

	function clamp( value ) {
		if ( value < MIN ) {
			return MIN;
		}

		if ( value > MAX ) {
			return MAX;
		}

		return value;
	}

	function init( frame ) {
		var after = frame.querySelector( '[data-beforeafter-after]' );

		if ( ! after ) {
			return;
		}

		var root = frame.closest( '.listing-beforeafter' );

		if ( ! root ) {
			return;
		}

		var handle = document.createElement( 'button' );
		handle.type = 'button';
		handle.className = 'listing-beforeafter__handle';
		// The handle IS the control, so it carries the slider semantics rather
		// than being decoration next to an invisible one.
		handle.setAttribute( 'role', 'slider' );
		handle.setAttribute( 'aria-valuemin', String( MIN ) );
		handle.setAttribute( 'aria-valuemax', String( MAX ) );
		handle.setAttribute( 'aria-label', frame.getAttribute( 'data-beforeafter-label' ) || 'Před a potom' );
		frame.appendChild( handle );

		var position = 50;
		var dragging = false;

		function apply( next ) {
			position = clamp( next );
			// One custom property drives both the clip and the handle offset,
			// so they cannot drift apart. Set on the frame, not the root: a
			// tabbed root holds several frames, each with its own position.
			frame.style.setProperty( '--beforeafter-position', position + '%' );
			handle.setAttribute( 'aria-valuenow', String( Math.round( position ) ) );
		}

		function fromClientX( clientX ) {
			var rect = frame.getBoundingClientRect();

			if ( 0 === rect.width ) {
				return;
			}

			apply( ( ( clientX - rect.left ) / rect.width ) * 100 );
		}

		frame.addEventListener( 'pointerdown', function ( event ) {
			dragging = true;
			// Pointer capture keeps the drag alive when the cursor leaves the
			// frame, which is what makes dragging to 0% or 100% possible.
			if ( frame.setPointerCapture ) {
				frame.setPointerCapture( event.pointerId );
			}
			fromClientX( event.clientX );
			event.preventDefault();
		} );

		frame.addEventListener( 'pointermove', function ( event ) {
			if ( ! dragging ) {
				return;
			}

			fromClientX( event.clientX );
		} );

		frame.addEventListener( 'pointerup', function () {
			dragging = false;
		} );

		frame.addEventListener( 'pointercancel', function () {
			dragging = false;
		} );

		handle.addEventListener( 'keydown', function ( event ) {
			var step = event.shiftKey ? 10 : 2;

			if ( 'ArrowLeft' === event.key ) {
				apply( position - step );
			} else if ( 'ArrowRight' === event.key ) {
				apply( position + step );
			} else if ( 'Home' === event.key ) {
				apply( MIN );
			} else if ( 'End' === event.key ) {
				apply( MAX );
			} else {
				return;
			}

			event.preventDefault();
		} );

		root.classList.add( 'listing-beforeafter--live' );
		apply( position );
	}

	var groupCount = 0;

	/**
	 * Turns a tabbed group's stacked panels into tabs.
	 *
	 * The server renders every pair visible under its own heading (see
	 * ListingDetail::render_before_after_tabs()); here each heading becomes a
	 * tab and every panel but the selected one is hidden. The pairs after the
	 * first are rendered loading="lazy", and a lazy image under a hidden
	 * ancestor is not fetched — so opening a tab is what loads its photos.
	 *
	 * WAI-ARIA tabs pattern: roving tabindex, arrow keys / Home / End move
	 * and select.
	 */
	function initTabs( group ) {
		var panels = group.querySelectorAll( '[data-beforeafter-panel]' );

		if ( 2 > panels.length ) {
			return;
		}

		groupCount += 1;

		var list = document.createElement( 'div' );
		var tabs = [];

		list.className = 'listing-beforeafter__tabs';
		list.setAttribute( 'role', 'tablist' );

		Array.prototype.forEach.call( panels, function ( panel, i ) {
			var heading = panel.querySelector( '[data-beforeafter-panel-heading]' );
			var id = 'listing-beforeafter-' + groupCount + '-' + i;
			var tab = document.createElement( 'button' );

			tab.type = 'button';
			tab.className = 'listing-beforeafter__tab';
			tab.id = id + '-tab';
			tab.setAttribute( 'role', 'tab' );
			tab.setAttribute( 'aria-controls', id );
			tab.textContent = heading ? heading.textContent.trim() : String( i + 1 );

			panel.id = id;
			panel.setAttribute( 'role', 'tabpanel' );
			panel.setAttribute( 'aria-labelledby', tab.id );

			// The tab now names the panel; keeping the heading too would
			// print every title twice.
			if ( heading ) {
				heading.hidden = true;
			}

			list.appendChild( tab );
			tabs.push( tab );
		} );

		function select( index, focus ) {
			tabs.forEach( function ( tab, i ) {
				var active = i === index;

				tab.setAttribute( 'aria-selected', active ? 'true' : 'false' );
				tab.tabIndex = active ? 0 : -1;
				tab.classList.toggle( 'listing-beforeafter__tab--active', active );
				panels[ i ].hidden = ! active;
			} );

			if ( focus ) {
				tabs[ index ].focus();
			}
		}

		list.addEventListener( 'click', function ( event ) {
			var tab = event.target.closest( '[role="tab"]' );

			if ( tab ) {
				select( tabs.indexOf( tab ), false );
			}
		} );

		list.addEventListener( 'keydown', function ( event ) {
			var current = tabs.indexOf( document.activeElement );
			var next;

			if ( -1 === current ) {
				return;
			}

			if ( 'ArrowRight' === event.key ) {
				next = ( current + 1 ) % tabs.length;
			} else if ( 'ArrowLeft' === event.key ) {
				next = ( current - 1 + tabs.length ) % tabs.length;
			} else if ( 'Home' === event.key ) {
				next = 0;
			} else if ( 'End' === event.key ) {
				next = tabs.length - 1;
			} else {
				return;
			}

			event.preventDefault();
			select( next, true );
		} );

		group.insertBefore( list, panels[ 0 ] );
		select( 0, false );
	}

	Array.prototype.forEach.call( document.querySelectorAll( '[data-beforeafter-tabs]' ), initTabs );

	var frames = document.querySelectorAll( '[data-beforeafter]' );

	Array.prototype.forEach.call( frames, init );
}() );
